import { isPlatformBrowser } from '@angular/common';
import {
  Component, Inject, OnDestroy, OnInit, PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { exhaustMap, interval, startWith, Subscription,  takeWhile } from 'rxjs';
import {
  Address, CeidgExtendedReportData,
} from '../../../../models/ceidg.model';
import {
  KrsExtendedReportCompanyAuthoritiesData, KrsExtendedReportPropertyRightsData,
} from '../../../../models/krs.model';
import {
  CrbrExtendedReportData,
  DeceasedReport,
  MsigExtendedReportData,
  ReportResultStatus,
  ReportStatus,
  ReportType,
  SudopExtendedReportData,
  VatWlExtendedReportData,
} from '../../../../models/report.model';
import { ReportService } from '../../services/report.service';
import {
  mapDebtRecordValue, mapLegalFormValue, mapPropertyRightsTextValue,
} from '../../utils/report-extended-mappers';
import { extendedReportExample } from '../../../../examples/extended-report.example';
import { getDateDaysAgo } from '../../../shared/utils/date.utils';

@Component({
  selector: 'app-report-mid',
  templateUrl: './report-extended.component.html',
  styleUrls: ['./report-extended.component.scss'],
})
export class ReportExtendedComponent implements OnInit, OnDestroy {
  public isLoading = true;
  public report: Extract<DeceasedReport, { type: ReportType.EXTENDED }> | null = null;
  private _reportId = '';
  private _subscription!: Subscription;
  public useMockData = false;
  public reportDeleyed = false;


  get progress(): number {

    if (!this.report?.lookup) {
      return 0;
    }

    const lookups = Object.values(this.report.lookup);
    const completedLookups = lookups.filter((lookup: any) =>
      ['PROCESSED', 'SKIPPED'].includes(lookup.status)
    ).length;
    const lookupProgress = (completedLookups / lookups.length) * 90;
    const fileProgress = this.report.reportFile ? 10 : 0;
    return Math.round(lookupProgress + fileProgress);
  }

  constructor(private _reportService: ReportService, private _route: ActivatedRoute, @Inject(PLATFORM_ID) private platformId: Object,) {
  }

  ngOnInit() {
    this.useMockData = this._route.snapshot.data['mockData'];
    this._reportId = this._route.snapshot.paramMap.get('id') ?? '';

    if (!this._reportId) {
      this.useMockData = true;
    }

    if (this.useMockData) {
      this.report = extendedReportExample;
      this._reportId = '';
      this.isLoading = false;
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this._subscription = interval(3000)
        .pipe(
          startWith(0),
          exhaustMap(() => this._reportService.getReport(this._reportId)),
          takeWhile(
            response => response.data.status !== ReportStatus.PROCESSED,
            true
          )
        )
        .subscribe({
          next: response => {
            this.isLoading = false;

            if (response.data.type !== ReportType.EXTENDED) {
              this.report = null;
              this._unsubscribe();
              return;
            }

            this.report = response.data;
            this.reportDeleyed = this.isReportDelayed(response.data.createdAt);

            if (this.report.status === ReportStatus.PROCESSED) {
              this._unsubscribe();
            }
          },
          error: error => {
            console.error(error);
            this.report = null;
            this.isLoading = false;
            this._unsubscribe();
          }
        });
    }
  }


  // ngOnInit() {
  //   this.useMockData = this._route.snapshot.data['mockData'];
  //   this._reportId = this._route.snapshot.params['id'];
  //
  //
  //
  //   if (!this._reportId) {
  //     this.useMockData = true;
  //   }
  //
  //   if (this.useMockData) {
  //     this.report = extendedReportExample;
  //     this._reportId = '';
  //     return;
  //   }
  //
  //   if (isPlatformBrowser(this.platformId)) {
  //     this._subscription = interval(1000)
  //       .pipe(startWith(0), switchMap(() => this._reportService.getReport(this._reportId)), takeWhile((response) => response.data.status !== ReportStatus.PROCESSED, true,),)
  //       .subscribe((response) => {
  //         if (response.data.type != ReportType.EXTENDED) {
  //           this._unsubscribe();
  //           return;
  //         }
  //         this.report = response.data;
  //         this.reportDeleyed = this.isReportDelayed(response.data.createdAt);
  //         if (this.report.status === ReportStatus.PROCESSED) {
  //           this._unsubscribe();
  //         }
  //       });
  //   }
  // }

  ngOnDestroy() {
    this._unsubscribe();
  }

  private isReportDelayed(createdAt: Date | string): boolean {
    const createdAtDate = new Date(createdAt);

    if (isNaN(createdAtDate.getTime())) {
      return false;
    }
    return Date.now() - createdAtDate.getTime() > 30 * 60 * 1000;
  }

  public mapReportResult(result: ReportResultStatus | undefined) {
    if (!result) {
      return '—';
    }
    return this._reportService.mapPrimaryReportResult(result);
  }

  public getDBStatusDate(date: Date): Date | null {
    return getDateDaysAgo(date, 1);
  }

  public generatePdf() {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.useMockData) {

      this._downloadExamplePDF();
      return;
    }
    if(!this.report) {
      return;
    }
    this._reportService.getExtendedReportPDF(this.report, this._reportId);


  }

  public getCeidgRows(): CeidgExtendedReportData[] {
    const data = this.report?.result?.ceidg?.data;
    return Array.isArray(data) ? data : [];
  }

  public getCeidgEmptyStateText(): string {
    return this.report?.result?.ceidg?.status === ReportResultStatus.SKIPPED ? this.mapReportResult(ReportResultStatus.SKIPPED) : 'Brak wpisów CEIDG.';
  }

  public getKrsPropertyRightsEmptyStateText(): string {
    return this.report?.result?.krs?.propertyRights?.status === ReportResultStatus.SKIPPED ? this.mapReportResult(ReportResultStatus.SKIPPED) : 'Brak wpisów dotyczących praw własności.';
  }

  public getKrsCompanyAuthoritiesEmptyStateText(): string {
    return this.report?.result?.krs?.companyAuthorities?.status === ReportResultStatus.SKIPPED ? this.mapReportResult(ReportResultStatus.SKIPPED) : 'Brak wpisów dotyczących funkcji we władzach spółek.';
  }

  public getKrsPropertyRightsRows(): KrsExtendedReportPropertyRightsData[] {
    const data = this.report?.result?.krs?.propertyRights?.data;
    return Array.isArray(data) ? data : [];
  }

  public getKrsCompanyAuthoritiesRows(): KrsExtendedReportCompanyAuthoritiesData[] {
    const data = this.report?.result?.krs?.companyAuthorities?.data;
    return Array.isArray(data) ? data : [];
  }

  public getCrbrRows(): CrbrExtendedReportData[] {
    const data = this.report?.result?.crbr?.data;
    return Array.isArray(data) ? data : [];
  }

  public getMsigRows(): MsigExtendedReportData[] {
    const data = this.report?.result?.msig?.data;
    return Array.isArray(data) ? data : [];
  }

  public getVatData(): VatWlExtendedReportData | null {
    const data = this.report?.result?.vatWl?.data;
    return data && !Array.isArray(data) ? data : null;
  }

  public getSudopData(): SudopExtendedReportData | null {
    const data = this.report?.result?.sudop?.data;
    return data && !Array.isArray(data) ? data : null;
  }

  public formatAddress(address?: Address): string {
    if (!address) {
      return '—';
    }

    const streetPart = [address.street, address.buildingNumber]
      .filter(Boolean)
      .join(' ');
    const localPart = address.localNumber ? `/${address.localNumber}` : '';
    const postalPart = [address.postalCode, address.city]
      .filter(Boolean)
      .join(' ');
    const full = [streetPart + localPart, postalPart]
      .filter(Boolean)
      .join(', ');
    return full || '—';
  }

  public mapLegalForm(legalForm: string): string {
    return mapLegalFormValue(legalForm);
  }

  public mapDebtRecord(hasDebtRecord: unknown): string {
    return mapDebtRecordValue(hasDebtRecord);
  }

  public getPropertyRightsText(item: KrsExtendedReportPropertyRightsData,): string {
    return mapPropertyRightsTextValue(item);
  }

  public getArrayLength(data: unknown): number {
    return Array.isArray(data) ? data.length : 0;
  }

  public getOtherDbsRows(): Array<{
    title: string; source: string; description: string; status: ReportResultStatus | undefined; details: string;
  }> {
    const sudop = this.getSudopData();
    const vat = this.getVatData();
    const crbrRows = this.getCrbrRows();
    const msigRows = this.getMsigRows();

    const sudopDetails = sudop?.totalAmount != null ? `Kwota pomocy: ${Number(sudop.totalAmount).toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} PLN` : '—';

    const vatAccounts = Array.isArray(vat?.accountNumbers) ? vat.accountNumbers : [];
    const vatDetailsParts = [vat?.nip ? `NIP: ${vat.nip}` : '', vat?.statusVat ? `Status VAT: ${vat.statusVat}` : '', vatAccounts.length ? `Rachunki: ${vatAccounts.join(', ')}` : '',].filter(Boolean);

    const uniqueCrbrRows = Array.from(
      new Map(
        crbrRows.map((entry) => [
          `${entry.name}-${entry.krs}`,
          entry,
        ]),
      ).values(),
    );

    const first = uniqueCrbrRows[0];

    const firstLabel = first
      ? `${first.name || '—'}${first.krs ? ` (KRS: ${first.krs})` : ''}`
      : '—';

    const remaining = uniqueCrbrRows.length - 1;

    const crbrDetails = uniqueCrbrRows.length
      ? remaining > 0
        ? `${firstLabel} +${remaining} wpisy`
        : firstLabel
      : '—';

    // const crbrDetails = crbrRows.length ? crbrRows
    //   .map((entry) => `${entry.name || '—'}${entry.krs ? ` (KRS: ${entry.krs})` : ''}`,)
    //   .join('; ') : '—';


    const msigCount = msigRows.length;

    const msigLabel =
      msigCount === 1
        ? 'ogłoszenie'
        : msigCount % 10 >= 2 &&
        msigCount % 10 <= 4 &&
        !(msigCount % 100 >= 12 && msigCount % 100 <= 14)
          ? 'ogłoszenia'
          : 'ogłoszeń';

    const msigDetails = msigCount
      ? `${msigCount} ${msigLabel}${
        msigRows[0]?.monitorNumber
          ? ` (np. ${msigRows[0].monitorNumber})`
          : ''
      }`
      : '—';

    // const msigDetails = msigRows.length ? `${msigRows.length} ogłoszeń` + (msigRows[0]?.monitorNumber ? ` (np. ${msigRows[0].monitorNumber})` : '') : '—';

    return [{
      title: 'Czy osoba zmarła otrzymała pomoc publiczną?',
      source: 'SUDOP',
      description: 'Pewne formy pomocy publicznej np. odroczenie zapłaty mandatu lub kary, mogą być źrodłem długów osoby zmarłej jeżeli nie zostały zapłacone.',
      status: this.report?.result?.sudop?.status,
      details: sudopDetails,
    }, {
      title: 'Czy osoba zmarła występuje w Wykazie Podatników VAT (Biała Lista)?',
      source: 'Wykaz Podatników VAT',
      description: 'To jest informacja o posiadaniu przez osobę zmarłą statusu podatnika podatku VAT. Rodzaj tego statusu może oznaczać posiadanie aktywów albo zobowiązań np. wobec US.',
      status: this.report?.result?.vatWl?.status,
      details: vatDetailsParts.join(' | ') || '—',
    }, {
      title: 'Czy osoba zmarła występuje w Krajowym Rejestrze Zadłużonych?',
      source: 'Krajowy Rejestr Zadłużonych',
      description: 'Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez nią długów. To jest bardzo istotna informacja.',
      status: this.report?.result?.krz?.status,
      details: '—',
    }, {
      title: 'Czy osoba zmarła jest beneficjentem spółki, trustu lub fundacji rodzinnej?',
      source: 'Centralny Rejestr Beneficjentów Rzeczywistych',
      description: 'Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez nią aktywów',
      status: this.report?.result?.crbr?.status,
      details: crbrDetails,
    }, {
      title: 'Czy osoba zmarła występuje w Monitorze Sądowym i Gospodarczym?',
      source: 'Monitor Sądowy i Gospodarczy',
      description: 'Występowanie osoby zmarłej w MSiG może oznaczać posiadanie przez nią aktywów, ale i potencjalnych długów',
      status: this.report?.result?.msig?.status,
      details: msigDetails,
    },];
  }

  private _unsubscribe() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  private _downloadExamplePDF(): void {
    const link = document.createElement('a');
    link.href = 'assets/downloads/Raport_Rozszerzony-przyklad.pdf';
    link.download = 'Raport_Rozszerzony-przyklad.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }



  //
  // ngOnInit() {
  //   registerLocaleData(es);
  //
  //   this.companies = [
  //     {name: 'Usługi Konsultingowe', type: 'Sp. z o.o.', shares: 30, value: 150000, krs: '00000000', entries: 'TAK', position: 'Prezes'},
  //     {name: 'Expotrans', type: 'S.A.', shares: 100, value: 9000, krs: '00000000', entries: 'TAK', position: 'Członek' +
  //         ' Zarządu'},
  //     {name: 'Kancelaria Prawna Polska', type: 'Sp. Komandytowo Akcyjna', shares: 25, value: 350000, krs: '00000000', entries: 'NIE', position: 'Komplementariusz'},
  //   ];
  //
  //   this._reportService.gerFreeDefinitions().subscribe(data => {
  //     this.definitions = data;
  //   })
  // }
  //
  // generatePdf() {
  //   const docDefinition: any = {
  //     content: [{text: "Sytuacja osoby zmarłej w publicznych bazach danych.", style: "header", alignment: "center"}, {
  //       table: {
  //         widths: ['*', '*'], body: [[{
  //           bold: true, text: "Osoba zmarła: Jan Kowalski"
  //         }, ""], ["PESEL: 1234566", ""], ["NIP: 1234567", "Wygenerowano na podstawie informacji z publicznych baz danych"], ["Data zgonu: 12/12/2022", "Data wygenerowania informacji z baz danych: 11.11.2023 - 09:54"],],
  //       }, layout: {
  //         fillColor: '#f7f7f7',
  //         hLineWidth: () => 0,
  //         vLineWidth: () => 0,
  //         border: [false, false, false, false],
  //         paddingLeft: (i: number) => i === 0 ? 10 : 0,
  //         paddingRight: (i: number) => i === 1 ? 10 : 0,
  //         paddingTop: (i: number) => i === 0 ? 10 : 0,
  //         paddingBottom: (i: number) => i === 0 ? 10 : 0,
  //       },
  //     }, {text: "CZY OSOBA ZMARŁA PROWADZIŁA DZIAŁALNOŚĆ GOSPODARCZĄ: TAK", margin: [0, 20, 0, 5], bold: true},
  //     {text: "Baza: Centralna Ewidencja i Informacja o Działalności Gospodarczej.", margin: [0, 5, 0, 5]},
  //     {text: "Prowadzenie działalności gospodarczej wiąże się z możliwością występowania aktywów, ale również z możliwością występowania zadłużenia w stosunku do administracji publicznej np. ZUS, US lub kontrahentów. Szczegóły w załączniku nr 1", margin: [0, 0, 0, 5]},
  //     {
  //       table: {
  //         headerRows: 1, widths: ['*', '*', '*', '*'], body: [
  //           ["Nazwa firmy", "NIP", "Status", "Spółka cywilna"],
  //           ["Marcin Molak Business Consulting", "1234567", "Zawieszony", "Nie"],
  //           ["Miejsce wykonywania działalności", {text: "ul. Marszałkowska 1 m.123, 03-123 Warszawa", colSpan: 3}],
  //       ],
  //       },
  //       alignment: 'center',
  //       layout: {
  //         fillColor: function (rowIndex: any) {
  //           return (rowIndex % 2 === 0) ? '#f7f7f7' : null;
  //         },
  //         hLineColor: () => '#c9c9c9',
  //         vLineColor: () => '#c9c9c9',
  //         border: [true, true, true, true],
  //         paddingLeft: () => 5,
  //         paddingRight: () => 5,
  //         paddingTop: () => 5,
  //         paddingBottom: () => 5,
  //       },
  //     }, {text: "CZY OSOBA ZMARŁA POSIADAŁA PRAWA WŁASNOŚCI W SPÓŁKACH?* TAK", margin: [0, 20, 0, 5], bold: true},
  //       {text: "Baza: Centralna Ewidencja i Informacja o Działalności Gospodarczej.", margin: [0, 5, 0, 5]},
  //       {text: "Posiadanie udziałów w spółkach jest aktywem niemniej jednak jest to uzależnione od kondycji danej spółki. Szczegóły w załączniku nr 2.", margin: [0, 5, 0, 5]}, {
  //       table: {
  //         headerRows: 1, widths: ['*', '*', 'auto', 'auto', '*', '*'], body: [
  //           ["Nazwa", "Forma prawna", "% udziałów lub akcji", "Wartość udziałów lub akcji w PLN", "KRS", "Wpis o zaległościach, wierzytelnościach, likwidacji?***"],
  //          ...this.companies.map(el => [
  //             {text: el.name},
  //             {text: el.type},
  //             {text: el.shares},
  //             {text: el.value},
  //             {text: el.krs},
  //             {text: el.entries},
  //         ]),
  //         ],
  //       }, layout: {
  //         fillColor: (rowIndex: any) => {
  //           return (rowIndex % 2 === 0) ? '#f7f7f7' : null;
  //         },
  //         hLineColor: () => '#c9c9c9',
  //         vLineColor: () => '#c9c9c9',
  //         border: [false, true, false, false],
  //         paddingLeft: () => 5,
  //         paddingRight: () => 5,
  //         paddingTop: () => 5,
  //         paddingBottom: () => 5,
  //       },
  //     }, {
  //       text: "*Prawa własności to np. udziały lub akcje. Pamiętaj, że jeżeli osoba zmarła posiadała samodzielnie lub łącznie z innymi osobami mniej niż 10% udziałów spółki z ograniczoną odpowiedzialnością lub nie posiadała wszystkich akcji spółki akcyjnej to takie informacje nie są publikowane i nie są widoczne w Dziale 1 KRS. Dlatego nawet jeżeli powyżej nie ukazuje się żadna spółka z ograniczoną odpowiedzialnością lub spółka akcyjna to nie oznacza to, że osoba zmarła nie była udziałowcem lub akcjonariuszem żadnego przedsiębiorstwa.",
  //       italics: true,
  //       margin: [0, 10, 0, 0]
  //     }, {
  //       text: " *** To jest informacja o potencjalnym zadłużeniu spółki. Znajdziesz ja w Dziale 4 wypisu z KRS w załączniku nr 2",
  //       italics: true,
  //       margin: [0, 10, 0, 0]
  //     }, {text: "CZY OSOBA ZMARŁA PEŁNIŁA FUNKCJE WE WŁADZACH SPÓŁKI NP. JAKO CZŁONEK ZARZĄDU SPÓŁKI LUB JEJ WSPÓLNIK?", margin: [0, 20, 0, 5], bold: true},
  //     {text: "Baza: Krajowy Rejestr Sądowy.", margin: [0, 5, 0, 5]},
  //     {text: "Posiadanie udziałów w spółkach jest aktywem niemniej jednak jest to uzależnione od kondycji danej spółki. Szczegóły w załączniku nr 2.", margin: [0, 5, 0, 5]},
  //     {
  //       table: {
  //         headerRows: 1, widths: ['*', '*', '*', '*'], body: [
  //           ["Nazwa", "Forma prawna", "KRS", "Funkcja"],
  //          ...this.companies.map(el => [
  //             {text: el.name},
  //             {text: el.type},
  //             {text: el.krs},
  //             {text: el.position},
  //         ]),
  //         ],
  //       }, layout: {
  //         fillColor: (rowIndex: any) => {
  //           return (rowIndex % 2 === 0) ? '#f7f7f7' : null;
  //         },
  //         hLineColor: () => '#c9c9c9',
  //         vLineColor: () => '#c9c9c9',
  //         border: [false, true, false, false],
  //         paddingLeft: () => 5,
  //         paddingRight: () => 5,
  //         paddingTop: () => 5,
  //         paddingBottom: () => 5,
  //       },
  //     },
  //     {text: "CZY OSOBA ZMARŁA WYSTĘPUJE W POZOSTAŁYCH BAZACH DANYCH?", margin: [0, 20, 0, 5], bold: true},
  //     {text: " Fakt występowania w poniższych bazach danych lub publikatorach może wskazywać na zródło długów lub majątku osoby zmarłej.", margin: [0, 5, 0, 5]},
  //     {
  //       table: {
  //         headerRows: 1, widths: ['*', 'auto', 'auto'], body: [
  //           [{text: [
  //             {text: "CZY OSOBA ZMARŁA OTRZYMAŁA POMOC PUBLICZNĄ?\n", bold: true},
  //             "Baza: SUDOP.\n",
  //             "Pewne formy pomocy publicznej np. odroczenie zapłaty mandatu lub kary, mogą być źrodłem długów osoby zmarłej jeżeli nie zostały zapłacone. Szczegóły w załączniku nr 3."
  //           ]}, "TAK", "Kwota 12.340 PLN"],
  //           [{text: [
  //             {text: "CZY OSOBA ZMARŁA WYSTĘPUJE W WYKAZIE PODATNIKÓW PODATKU VAT, CZYLI BIAŁEJ LIŚCIE PODATNIKÓW VAT?\n", bold: true},
  //             "Baza: Wykaz Podatników Podatku VAT.\n",
  //             "To jest informacja o posiadaniu przez osobę zmarłą statusu podatnika podatku VAT. Rodzaj tego statusu może oznaczać posiadanie aktywów albo zobowiązań np. wobec US. Szczegóły w załączniku nr 4.",
  //           ]}, "TAK", "Nr. NIP 1130618182"],
  //           [{text: [
  //             {text: "CZY OSOBA ZMARŁA WYSTĘPUJE SIE W KRAJOWYM REJESTRZE ZADŁUŻONYCH?\n", bold: true},
  //             "Baza: Krajowy Rejestr Zadłużonych.\n",
  //             "Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez nią długów. To jest bardzo istotna informacja. Szczegóły w załączniku nr 5.",
  //           ]}, {text: "NIE", colSpan: 2}],
  //           [{text: [
  //             {text: "CZY OSOBA ZMARŁA JEST BENEFICJENTEM SPÓŁKI, TRUSTU LUB FUNDACJI RODZINNEJ?\n", bold: true},
  //             "Baza: Centralny Rejestr Beneficjentów Rzeczywistych.\n",
  //             "Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez nią aktywów. Szczegóły w załączniku nr 6",
  //           ]}, "TAK", "Aurexa Sp. z o.o. Marszałkowska 111 Warszawa NIP: Rodzaj:"],
  //           [{text: [
  //             {text: "CZY OSOBA ZMARŁA WYSTĘPUJE W MONITORZE SĄDOWYM I GOSPODARCZYM?\n", bold: true},
  //             "Baza: Monitor Sądowy i Gospodarczy\n",
  //             "Występowanie osoby zmarłej w MSiG może oznaczać posiadanie przez nią aktywów, ale i potencjalnych długów. Szczegóły w załączniku nr 7. ",
  //           ]}, {text: "NIE", colSpan: 2}],
  //         ],
  //       }
  //     },
  //     {
  //       table: {
  //         headerRows: 1, widths: ['100%'], body: [[{
  //           bold: true, text: "Definicje",
  //         }], ...this.definitions.map(el => [{
  //           text: [{text: `${el.title}\n\n`, bold: true, margin: [0, 5, 0, 0]}, {text: el.desc}],
  //         }]),],
  //       }, layout: {
  //         fillColor: (rowIndex: any) => {
  //           return (rowIndex % 2 === 0) ? '#f7f7f7' : null;
  //         },
  //         hLineColor: () => '#c9c9c9',
  //         vLineColor: () => '#c9c9c9',
  //         border: [false, true, false, false],
  //         paddingLeft: () => 5,
  //         paddingRight: () => 5,
  //         paddingTop: () => 5,
  //         paddingBottom: () => 5,
  //       }, margin: [0, 20, 0, 5],
  //     },], styles: {
  //       header: {
  //         fontSize: 18, bold: true, margin: [0, 0, 0, 20]
  //       }, table: {
  //         margin: [0, 5, 0, 5],
  //       },
  //     }, header: function (): any {
  //       return {
  //         table: {
  //           body: [[{
  //             svg: '<svg version="1.0" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0" y="0" width="2400" height="562.1704344824068" viewBox="80.6834327697754 104.07868881225586 178.63314971923828 41.84261474609375" preserveAspectRatio="xMidYMid meet" color-interpolation-filters="sRGB"><g><defs><linearGradient id="92" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#fa71cd"></stop> <stop offset="100%" stop-color="#9b59b6"></stop></linearGradient><linearGradient id="93" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f9d423"></stop> <stop offset="100%" stop-color="#f83600"></stop></linearGradient><linearGradient id="94" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#0064d2"></stop> <stop offset="100%" stop-color="#1cb0f6"></stop></linearGradient><linearGradient id="95" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f00978"></stop> <stop offset="100%" stop-color="#3f51b1"></stop></linearGradient><linearGradient id="96" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#7873f5"></stop> <stop offset="100%" stop-color="#ec77ab"></stop></linearGradient><linearGradient id="97" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f9d423"></stop> <stop offset="100%" stop-color="#e14fad"></stop></linearGradient><linearGradient id="98" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#009efd"></stop> <stop offset="100%" stop-color="#2af598"></stop></linearGradient><linearGradient id="99" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#ffcc00"></stop> <stop offset="100%" stop-color="#00b140"></stop></linearGradient><linearGradient id="100" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#d51007"></stop> <stop offset="100%" stop-color="#ff8177"></stop></linearGradient><linearGradient id="102" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#a2b6df"></stop> <stop offset="100%" stop-color="#0c3483"></stop></linearGradient><linearGradient id="103" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#7ac5d8"></stop> <stop offset="100%" stop-color="#eea2a2"></stop></linearGradient><linearGradient id="104" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#00ecbc"></stop> <stop offset="100%" stop-color="#007adf"></stop></linearGradient><linearGradient id="105" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#b88746"></stop> <stop offset="100%" stop-color="#fdf5a6"></stop></linearGradient></defs><g fill="#4a93fa" class="iconlinesvg-g iconlinesvg" transform="translate(82.43473827838898,105.82850074768066)"><g class="tp-name"><g transform="translate(0, 9.766119447036946)"><g><g class="imagesvg"><g><rect fill="#4a93fa" fill-opacity="0" stroke-width="2" x="0" y="0" width="32.32437316467133" height="18.809753425781544" class="image-rect"></rect> <svg x="0" y="0" width="32.32437316467133" height="18.809753425781544" filtersec="colorsb3681624400" class="image-svg-svg primary" style="overflow: visible;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="-0.0008234977722167969 -0.0005435943603515625 166.71340942382812 97.01138305664062"><path d="M104.65 75.14c-.47 9.73-6.41 17.94-15.44 20.76s-19-.13-24-8.33Q48.47 59.55 32.54 31C26.93 20.92 31 8.48 40.88 2.94 51.15-2.84 63.6.06 69.64 10.24 80.49 28.53 91.06 47 101.53 65.49c1.63 2.89 2.11 6.42 3.12 9.65zM115.18 0a20.32 20.32 0 0 1 17.13 10.4c10.63 18.21 21.26 36.43 31.62 54.8a21.12 21.12 0 0 1-7.69 28.71c-10 5.93-22.81 3.21-28.87-6.92-11-18.38-21.83-36.9-32.13-55.69C87.48 17.14 98 .1 115.18 0z" fill="#4a93fa"></path><path d="M0 75.33C-.06 63 9.13 53.62 21.25 53.68a21.34 21.34 0 0 1 .06 42.68A20.85 20.85 0 0 1 0 75.33z" fill="#1378c0"></path></svg></svg> </g></g></g></g><g transform="translate(36, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M20.49-23.46L21.87-25.74C19.59-27.72 16.75-29.07 12.65-29.07C6.89-29.07 2.36-26.52 2.36-20.78C2.36-13.97 10.02-13.88 15.37-12.28C17.91-11.52 20.29-10.43 20.29-7.28C20.29-3.52 17.00-2.28 12.97-2.28C8.45-2.28 4.94-3.93 2.72-6.26L1.30-3.94C4.55-1.15 8.42 0.24 12.89 0.24C18.62 0.24 23.30-1.86 23.30-7.56C23.30-14.76 15.37-14.88 9.70-16.55C7.29-17.25 5.29-18.21 5.29-21.18C5.29-25.09 8.27-26.55 12.60-26.55C16.06-26.55 18.88-25.29 20.49-23.46Z" transform="translate(-1.3011068443641292, 29.681499887056702)"></path></g></g><g transform="translate(61, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M5.53-17.08L5.53-21.18L3.05-21.18L3.05 8.66L5.81 8.66L5.81-4.39C7.33-1.90 10.02 0.41 13.86 0.41C15.27 0.41 16.57 0.12 17.75-0.47C21.09-2.12 23.46-5.66 23.46-10.57C23.46-13.73 22.19-16.41 20.61-18.20C19.02-20.01 16.64-21.55 13.42-21.55C9.80-21.55 7.05-19.40 5.53-17.08ZM13.09-2.03C12.31-2.03 11.55-2.17 10.82-2.44C8.49-3.30 6.78-5.11 5.81-7.36L5.81-13.86C5.92-14.57 6.21-15.24 6.69-15.88C7.87-17.47 10.11-19.11 12.77-19.11C13.88-19.11 14.92-18.88 15.88-18.42C18.51-17.16 20.66-14.39 20.66-10.57C20.66-9.49 20.48-8.44 20.13-7.42C19.15-4.61 16.82-2.03 13.09-2.03Z" transform="translate(-3.0494691664784277, 29.681499887056702)"></path></g></g><g transform="translate(85, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M16.83-3.54L16.91-1.87C16.97-0.59 17.83 0.12 19.19 0.12C19.44 0.12 19.79 0.08 20.25 0L20.25-2.44C19.57-2.44 19.23-2.82 19.23-3.58L19.23-13.13C19.23-18.40 15.97-21.55 10.69-21.55C8.12-21.55 5.46-20.63 2.72-18.78L3.70-16.96C5.57-18.26 7.75-19.48 10.49-19.48C14.22-19.48 16.47-17.10 16.47-13.38L16.47-11.47C14.84-12.13 12.44-12.56 10.21-12.56C8.90-12.56 7.71-12.41 6.63-12.10C3.94-11.32 1.34-9.52 1.34-6.18C1.34-5.26 1.52-4.40 1.87-3.60C2.87-1.33 5.14 0.41 8.38 0.41C12.17 0.41 15.09-1.26 16.83-3.54ZM10.57-10.82C12.81-10.82 14.77-10.37 16.47-9.72L16.47-6.26C16.47-5.61 16.11-5.05 15.74-4.68C14.53-2.95 11.94-1.71 9.03-1.71C6.21-1.71 3.98-3.56 3.98-6.38C3.98-9.43 7.27-10.82 10.57-10.82Z" transform="translate(-1.3417664332505082, 29.681499887056702)"></path></g></g><g transform="translate(107, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M19.64-4.11L19.64-2.03C19.64-0.81 20.84 0.12 22.08 0.12C22.32 0.12 22.65 0.08 23.05 0L23.05-2.44C22.40-2.44 22.08-2.82 22.08-3.58L22.08-29.68L19.31-29.68L19.31-16.75C17.87-19.19 15.08-21.55 11.30-21.55C9.89-21.55 8.60-21.26 7.42-20.68C4.10-19.03 1.63-15.42 1.63-10.53C1.63-9.09 1.88-7.72 2.38-6.40C3.75-2.82 6.89 0.41 11.75 0.41C15.29 0.41 18.20-1.74 19.64-4.11ZM12.36-2.03C11.25-2.03 10.21-2.26 9.25-2.72C6.61-3.99 4.47-6.70 4.47-10.53C4.47-11.62 4.65-12.67 5.00-13.70C5.96-16.52 8.28-19.11 11.99-19.11C12.73-19.11 13.47-18.97 14.23-18.70C16.51-17.89 18.48-16.08 19.31-13.82L19.31-7.28C19.20-6.55 18.91-5.86 18.44-5.22C17.26-3.64 15.00-2.03 12.36-2.03Z" transform="translate(-1.6263835554551616, 29.681499887056702)"></path></g></g><g transform="translate(132, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M12.32-1.71C11.29-1.71 10.32-1.90 9.41-2.30C6.74-3.45 4.73-6.04 4.47-9.60L22.61-9.60C22.63-9.68 22.65-9.83 22.65-10.04L22.65-10.69C22.65-12.16 22.39-13.54 21.87-14.84C20.42-18.51 17.20-21.55 12.16-21.55C10.64-21.55 9.23-21.26 7.93-20.68C4.40-19.09 1.59-15.66 1.59-10.65C1.59-9.13 1.85-7.71 2.38-6.38C3.85-2.69 7.12 0.41 12.20 0.41C16.56 0.41 19.93-1.76 21.22-4.88L18.83-5.53C17.89-3.35 15.38-1.71 12.32-1.71ZM12.20-19.39C13.23-19.39 14.20-19.20 15.11-18.83C17.77-17.71 19.75-15.21 20.00-11.71L4.43-11.71C4.51-12.82 4.76-13.85 5.18-14.80C6.30-17.32 8.65-19.39 12.20-19.39Z" transform="translate(-1.5857239665687823, 29.681499887056702)"></path></g></g><g transform="translate(157, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M5.81 0L5.81-6.63L10.37-10.86L18.17 0L21.18 0L12.20-12.48L20.86-21.14L17.73-21.14L5.81-9.51L5.81-29.68L3.05-29.68L3.05 0Z" transform="translate(-3.049469166478428, 29.681499887056702)"></path></g></g></g> </g><defs v-gra="od"></defs></g></svg>',
  //             width: 125,
  //           }, {
  //             text: 'Numer: X123344678/2023', alignment: 'right',
  //           },]], widths: ['50%', '50%'],
  //         }, margin: [40, 40, 40, 0], layout: 'noBorders',
  //       };
  //     }, footer: function (currentPage: number, pageCount: number): any {
  //       return {
  //         table: {
  //           body: [[{text: `${currentPage}/${pageCount}`, alignment: 'right', margin: [0, 0, 40, 0]},]],
  //           widths: ['100%'],
  //         }, layout: 'noBorders'
  //       };
  //     }, pageMargins: [40, 100, 40, 40], defaultStyle: {font: 'nunito'},
  //   };
  //
  //   const fonts = {
  //       nunito: {
  //           normal: `${window.location.origin}/assets/webfonts/nunito-sans/NunitoSans_7pt_Condensed-Light.ttf`,
  //           bold: `${window.location.origin}/assets/webfonts/nunito-sans/NunitoSans_7pt_Condensed-Bold.ttf`,
  //           italics: `${window.location.origin}/assets/webfonts/nunito-sans/NunitoSans_7pt_Condensed-Italic.ttf`,
  //           bolditalics: `${window.location.origin}/assets/webfonts/nunito-sans/NunitoSans_7pt_Condensed-BoldItalic.ttf`,
  //       },
  //   };
  //   pdfMake.createPdf(docDefinition, undefined, fonts).download("test.pdf");
  // }
}
